import type { AssetCustomData, AssetCustomDataValue, AssetListItemDto } from "@/src/types/assetApi.types";
import type { CategoryChild, CategoryChildField, CategoryFieldType } from "@/src/types/category.types";
import { isIsoDateString } from "@/src/utils/assetDateConstraints";
import {
  formatMonthYearDisplay,
  isMonthYearIso,
  normalizeMonthYearFormValue,
} from "@/src/utils/datePicker";

export const formatDynamicFieldLabel = (rawKey: string): string =>
  rawKey
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const dynamicFieldKeysMatch = (fieldKey: string, candidateKey: string): boolean => {
  const normalizedFieldKey = fieldKey.trim().toLowerCase().replace(/[_\s-]+/g, "");
  const normalizedCandidateKey = candidateKey.trim().toLowerCase().replace(/[_\s-]+/g, "");

  if (!normalizedFieldKey || !normalizedCandidateKey) return false;

  return (
    normalizedFieldKey === normalizedCandidateKey ||
    normalizedFieldKey.includes(normalizedCandidateKey) ||
    normalizedCandidateKey.includes(normalizedFieldKey)
  );
};

export const isInstalledOnFieldKey = (key: string): boolean =>
  dynamicFieldKeysMatch(key, "installedOn") || dynamicFieldKeysMatch(key, "installDate");

export const stripInstalledOnFromCustomData = (customData: AssetCustomData): AssetCustomData => {
  const next = { ...customData };

  Object.keys(next).forEach((key) => {
    if (isInstalledOnFieldKey(key)) {
      delete next[key];
    }
  });

  return next;
};

type RawCategoryChildField = {
  id?: string;
  key?: string;
  name?: string;
  fieldType?: unknown;
};

export const normalizeCategoryFieldType = (value: unknown): CategoryFieldType => {
  const normalized = String(value ?? "TEXT")
    .trim()
    .toUpperCase()
    .replace(/-/g, "_");

  if (normalized === "ALPHANUMERIC") {
    return "ALPHANUMERIC";
  }
  if (normalized === "NUMBER") {
    return "NUMBER";
  }
  if (normalized === "BOOLEAN") {
    return "BOOLEAN";
  }
  if (normalized === "DATE") {
    return "DATE";
  }
  if (normalized === "YEAR") {
    return "YEAR";
  }
  if (normalized === "MONTH_YEAR" || normalized === "MONTHYEAR") {
    return "MONTH-YEAR";
  }

  return "TEXT";
};

export const normalizeCategoryChildField = (
  field: RawCategoryChildField,
): CategoryChildField => ({
  id: field.id ?? "",
  key: (field.key ?? field.name ?? "").trim(),
  fieldType: normalizeCategoryFieldType(field.fieldType),
});

export const normalizeCategoryChildren = (categories: CategoryChild[]): CategoryChild[] =>
  categories.map((category) => ({
    ...category,
    fields: (category.fields ?? []).map((field) =>
      normalizeCategoryChildField(field as RawCategoryChildField),
    ),
  }));

export type AssetCustomDataEntry = {
  key: string;
  value: string;
};

const LEGACY_FIELD_MAPPINGS: Array<{ sourceKey: keyof AssetListItemDto; keys: string[] }> = [
  { sourceKey: "brand", keys: ["brand", "Brand"] },
  { sourceKey: "model", keys: ["model", "Model"] },
  { sourceKey: "serialNumber", keys: ["serial", "Serial", "serialNumber", "Serial Number"] },
];

const normalizeKey = (key: string): string => key.trim().toLowerCase();

const customDataValueToFormString = (value: AssetCustomDataValue): string => {
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (typeof value === "number") {
    return String(value);
  }

  return value.trim();
};

export const coerceDynamicFieldToCustomDataValue = (
  fieldType: CategoryFieldType,
  rawValue: string,
): AssetCustomDataValue | undefined => {
  const normalizedType = normalizeCategoryFieldType(fieldType);
  const trimmed = rawValue.trim();

  if (normalizedType === "BOOLEAN") {
    if (trimmed === "") {
      return false;
    }

    if (trimmed !== "true" && trimmed !== "false") {
      return undefined;
    }

    return trimmed === "true";
  }

  if (!trimmed) {
    return undefined;
  }

  if (normalizedType === "NUMBER") {
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  if (normalizedType === "YEAR") {
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return trimmed;
};

export const getChildCategoryFields = (
  childCategories: CategoryChild[],
  childCategoryId: string,
): CategoryChildField[] => {
  const category = childCategories.find((item) => item.id === childCategoryId);
  return (category?.fields ?? []).map((field) => normalizeCategoryChildField(field));
};

export const pruneDynamicFieldsForCategory = (
  dynamicFields: Record<string, string>,
  fields: CategoryChildField[],
): Record<string, string> => {
  const allowedKeys = new Set(fields.map((field) => field.key));
  const next: Record<string, string> = {};

  Object.entries(dynamicFields).forEach(([key, value]) => {
    if (allowedKeys.has(key)) {
      next[key] = value;
    }
  });

  return next;
};

/** Initialize all schema fields for a sub-category; boolean fields default to "false". */
export const buildDefaultDynamicFieldsForCategory = (
  fields: CategoryChildField[],
  existing: Record<string, string> = {},
): Record<string, string> => {
  const next: Record<string, string> = {};

  fields.forEach((rawField) => {
    const field = normalizeCategoryChildField(rawField);
    const fieldType = normalizeCategoryFieldType(field.fieldType);
    const preserved = existing[field.key];

    if (fieldType === "BOOLEAN") {
      next[field.key] = preserved === "true" ? "true" : "false";
      return;
    }

    next[field.key] = preserved ?? "";
  });

  return next;
};

const readLegacyFieldValue = (
  item: AssetListItemDto,
  field: CategoryChildField,
): string | undefined => {
  const mapping = LEGACY_FIELD_MAPPINGS.find((entry) =>
    entry.keys.some((key) => normalizeKey(key) === normalizeKey(field.key)),
  );

  if (!mapping) {
    return undefined;
  }

  const value = item[mapping.sourceKey];
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export const buildDynamicFieldsFromAsset = (
  item: AssetListItemDto,
  fields: CategoryChildField[],
): Record<string, string> => {
  const dynamicFields: Record<string, string> = {};

  fields.forEach((field) => {
    if (isInstalledOnFieldKey(field.key)) {
      return;
    }

    const customEntry = Object.entries(item.customData ?? {}).find(
      ([key]) => normalizeKey(key) === normalizeKey(field.key),
    );
    const customValue = customEntry?.[1];
    if (customValue !== null && customValue !== undefined) {
      const formValue = customDataValueToFormString(customValue);
      if (formValue || typeof customValue === "boolean") {
        dynamicFields[field.key] = formValue;
        return;
      }
    }

    const legacyValue = readLegacyFieldValue(item, field);
    if (legacyValue) {
      dynamicFields[field.key] = legacyValue;
    }
  });

  return buildDefaultDynamicFieldsForCategory(fields, dynamicFields);
};

export const formatBooleanFieldValue = (value: string): string => {
  if (value === "true") {
    return "Yes";
  }
  if (value === "false") {
    return "No";
  }

  return value;
};

export const collectAssetCustomDataEntries = (item: AssetListItemDto): AssetCustomDataEntry[] => {
  const entries: AssetCustomDataEntry[] = [];
  const seenKeys = new Set<string>();

  const addEntry = (key: string, value?: AssetCustomDataValue | string | null) => {
    if (value === null || value === undefined) {
      return;
    }

    const displayValue =
      typeof value === "string" ? value.trim() : customDataValueToFormString(value);

    if (!displayValue && typeof value !== "boolean") {
      return;
    }

    const normalized = normalizeKey(key);
    if (seenKeys.has(normalized)) {
      return;
    }

    seenKeys.add(normalized);
    entries.push({
      key,
      value: typeof value === "boolean" ? formatBooleanFieldValue(displayValue) : displayValue,
    });
  };

  LEGACY_FIELD_MAPPINGS.forEach((mapping) => {
    const value = item[mapping.sourceKey];
    if (typeof value === "string") {
      addEntry(mapping.keys[0], value);
    }
  });

  Object.entries(item.customData ?? {}).forEach(([key, value]) => {
    if (isInstalledOnFieldKey(key)) {
      return;
    }

    addEntry(key, value);
  });

  return entries;
};

export const formatCustomDataEntriesLabel = (entries: AssetCustomDataEntry[]): string => {
  if (entries.length === 0) {
    return "";
  }

  return entries.map((entry) => `${entry.key}: ${entry.value}`).join(" · ");
};

export const formatDynamicFieldDisplayValue = (
  fieldType: CategoryFieldType,
  value: string,
): string => {
  const normalizedType = normalizeCategoryFieldType(fieldType);

  if (normalizedType === "BOOLEAN") {
    return formatBooleanFieldValue(value);
  }

  if (normalizedType === "DATE" && isIsoDateString(value.trim())) {
    const date = new Date(`${value.trim()}T12:00:00`);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
  }

  if (normalizedType === "YEAR") {
    return value.trim();
  }

  if (normalizedType === "MONTH-YEAR") {
    return formatMonthYearDisplay(value.trim());
  }

  return value;
};

const isAlphanumericValue = (value: string): boolean => /^[a-zA-Z0-9]+$/.test(value);

const isNumberValue = (value: string): boolean => /^-?\d+(\.\d+)?$/.test(value);

const isYearValue = (value: string): boolean => /^\d{4}$/.test(value);

const validationMessageForField = (
  field: CategoryChildField,
  fieldType: CategoryFieldType,
): string => {
  const label = field.key.trim() || "This field";

  if (fieldType === "ALPHANUMERIC") {
    return `${label} must contain only letters and numbers.`;
  }
  if (fieldType === "NUMBER") {
    return `${label} must be a valid number.`;
  }
  if (fieldType === "DATE") {
    return `${label} must be a valid date (YYYY-MM-DD).`;
  }
  if (fieldType === "YEAR") {
    return `${label} must be a 4-digit year.`;
  }
  if (fieldType === "MONTH-YEAR") {
    return `${label} must be a valid month and year (YYYY-MM).`;
  }
  if (fieldType === "BOOLEAN") {
    return `${label} must be Yes or No.`;
  }

  return `${label} is invalid.`;
};

type ValidateDynamicFieldOptions = {
  required?: boolean;
};

export const validateDynamicFieldValue = (
  field: CategoryChildField,
  value: string,
  options: ValidateDynamicFieldOptions = {},
): string | undefined => {
  const required = options.required ?? false;
  const fieldType = normalizeCategoryFieldType(field.fieldType);
  const trimmed = value.trim();
  const label = field.key.trim() || "This field";

  if (fieldType === "BOOLEAN") {
    if (!trimmed) {
      return required ? `${label} is required.` : undefined;
    }
    if (trimmed !== "true" && trimmed !== "false") {
      return validationMessageForField(field, fieldType);
    }

    return undefined;
  }

  if (!trimmed) {
    return required ? `${label} is required.` : undefined;
  }

  if (fieldType === "TEXT") {
    return undefined;
  }

  if (fieldType === "ALPHANUMERIC" && !isAlphanumericValue(trimmed)) {
    return validationMessageForField(field, fieldType);
  }

  if (fieldType === "NUMBER" && !isNumberValue(trimmed)) {
    return validationMessageForField(field, fieldType);
  }

  if (fieldType === "DATE" && !isIsoDateString(trimmed)) {
    return validationMessageForField(field, fieldType);
  }

  if (fieldType === "YEAR" && !isYearValue(trimmed)) {
    return validationMessageForField(field, fieldType);
  }

  if (fieldType === "MONTH-YEAR" && !isMonthYearIso(trimmed)) {
    return validationMessageForField(field, fieldType);
  }

  return undefined;
};

export const getDynamicFieldValidationHint = (fieldType: CategoryFieldType): string => {
  const normalizedType = normalizeCategoryFieldType(fieldType);

  if (normalizedType === "ALPHANUMERIC") {
    return "Letters and numbers only.";
  }
  if (normalizedType === "NUMBER") {
    return "Enter a valid number.";
  }
  if (normalizedType === "DATE") {
    return "Use YYYY-MM-DD format.";
  }
  if (normalizedType === "YEAR") {
    return "Enter a 4-digit year.";
  }
  if (normalizedType === "MONTH-YEAR") {
    return "Select a month and year.";
  }
  if (normalizedType === "BOOLEAN") {
    return "Toggle for Yes or No.";
  }

  return "";
};

export const validateDynamicFields = (
  fields: CategoryChildField[],
  dynamicFields: Record<string, string>,
  options: ValidateDynamicFieldOptions = {},
): Record<string, string> => {
  const required = options.required ?? true;
  const errors: Record<string, string> = {};

  fields.forEach((field) => {
    const normalizedField = normalizeCategoryChildField(field);
    const value = dynamicFields[normalizedField.key] ?? "";
    const error = validateDynamicFieldValue(normalizedField, value, { required });
    if (error) {
      errors[normalizedField.key] = error;
    }
  });

  return errors;
};

export const sanitizeDynamicFieldInput = (
  fieldType: CategoryFieldType,
  value: string,
): string => {
  const normalizedType = normalizeCategoryFieldType(fieldType);

  if (normalizedType === "ALPHANUMERIC") {
    return value.replace(/[^a-zA-Z0-9]/g, "");
  }

  if (normalizedType === "NUMBER") {
    const cleaned = value.replace(/[^\d.-]/g, "");
    const parts = cleaned.split(".");
    if (parts.length <= 1) {
      return cleaned;
    }

    return `${parts[0]}.${parts.slice(1).join("")}`;
  }

  if (normalizedType === "YEAR") {
    return value.replace(/\D/g, "").slice(0, 4);
  }

  if (normalizedType === "MONTH-YEAR") {
    return normalizeMonthYearFormValue(value);
  }

  return value;
};

export const buildDurationFromCategory = (
  childCategories: CategoryChild[],
  childCategoryId: string,
): string | undefined => {
  const category = childCategories.find((item) => item.id === childCategoryId);
  const lifecycleYears = category?.lifecycleMaxYears ?? category?.lifecycleMinYears;

  if (lifecycleYears === null || lifecycleYears === undefined) {
    return undefined;
  }

  return `${lifecycleYears} years`;
};

const hasCustomDataKey = (customData: AssetCustomData, targetKey: string): boolean =>
  Object.keys(customData).some((key) => normalizeKey(key) === normalizeKey(targetKey));

export const appendDurationToCustomData = (
  customData: AssetCustomData,
  childCategories: CategoryChild[],
  childCategoryId: string,
): AssetCustomData => {
  const duration = buildDurationFromCategory(childCategories, childCategoryId);
  if (!duration || hasCustomDataKey(customData, "Duration")) {
    return customData;
  }

  return { ...customData, Duration: duration };
};

export const buildCustomDataPayload = (
  fields: CategoryChildField[],
  dynamicFields: Record<string, string>,
  childCategories: CategoryChild[],
  childCategoryId: string,
): AssetCustomData => {
  const customData: AssetCustomData = {};

  fields.forEach((field) => {
    if (isInstalledOnFieldKey(field.key)) {
      return;
    }

    const fieldType = normalizeCategoryFieldType(field.fieldType);
    const rawValue = dynamicFields[field.key] ?? "";
    const coercedValue = coerceDynamicFieldToCustomDataValue(field.fieldType, rawValue);

    if (coercedValue !== undefined) {
      customData[field.key] = coercedValue;
      return;
    }

    if (fieldType === "BOOLEAN") {
      customData[field.key] = false;
    }
  });

  return appendDurationToCustomData(customData, childCategories, childCategoryId);
};

const removeCustomDataKey = (customData: AssetCustomData, targetKey: string): void => {
  Object.keys(customData).forEach((key) => {
    if (normalizeKey(key) === normalizeKey(targetKey)) {
      delete customData[key];
    }
  });
};

export const mergeCustomDataForUpdate = (
  fields: CategoryChildField[],
  dynamicFields: Record<string, string>,
  childCategories: CategoryChild[],
  childCategoryId: string,
  sourceCustomData?: AssetCustomData,
): AssetCustomData => {
  const nextCustomData: AssetCustomData = { ...(sourceCustomData ?? {}) };

  fields.forEach((field) => {
    removeCustomDataKey(nextCustomData, field.key);

    if (isInstalledOnFieldKey(field.key)) {
      return;
    }

    const fieldType = normalizeCategoryFieldType(field.fieldType);
    const rawValue = dynamicFields[field.key] ?? "";
    const coercedValue = coerceDynamicFieldToCustomDataValue(field.fieldType, rawValue);

    if (coercedValue !== undefined) {
      nextCustomData[field.key] = coercedValue;
      return;
    }

    if (fieldType === "BOOLEAN") {
      nextCustomData[field.key] = false;
    }
  });

  removeCustomDataKey(nextCustomData, "Duration");
  return stripInstalledOnFromCustomData(
    appendDurationToCustomData(nextCustomData, childCategories, childCategoryId),
  );
};
